import { getStringHash } from '@noths/polaris-client-utils';
import {
  Environment as ContentStackEnvs,
  fetchContentGraphQL,
  stackConfig,
} from '@noths/polaris-server-content-fetching';
import type bunyan from 'bunyan';

import { adaptGraphQLGlobalNavToCommonNav } from './graphql/adaptGraphQLGlobalNavToCommonNav';
import { adaptGraphQLTopBannerToCommon } from './graphql/adaptGraphQLTopBannerToCommon';
import type { ContentstackGlobalNavGraphQLData } from './graphql/contentstackGraphQLResponse';
import { globalNavigationAndTopBannerQuery } from './graphql/globalNavigationAndTopBannerQuery';
import type { NavigationProps } from 'client-site-chrome-ui/components/organisms/Navigation/Navigation';
import type { TopBannerMessage } from 'client-site-chrome-ui/content/types/TopBannerMessage';

export const Environment = ContentStackEnvs;

export const GLOBAL_NAVIGATION_QUERY_HASH = getStringHash(globalNavigationAndTopBannerQuery);

interface FetchNavigationConfig {
  env: ContentStackEnvs;
  logger?: bunyan;
}

export interface SiteChromeContent {
  navContent: NavigationProps['content'];
  topBannerMessages: TopBannerMessage[];
}

export const fetchSiteChromeContent = async (
  { env, logger }: FetchNavigationConfig,
  variantId = 'control',
): Promise<SiteChromeContent> => {
  const result = await fetchContentGraphQL(
    {
      deliveryToken: stackConfig.DX.envs[env].token,
      environment: stackConfig.DX.envs[env].name,
      query: globalNavigationAndTopBannerQuery,
      stackAPIKey: stackConfig.DX.apiKey,
      variables: {
        variantId,
      },
    },
    {
      cacheKey: `global-nav-${variantId}-${GLOBAL_NAVIGATION_QUERY_HASH}`,
      cacheTTLInSeconds: 300,
      timeout: 2000,
    },
  );

  if (logger && result.status === 'expired') {
    logger.warn('fetched navigation content is expired');
  }

  if (result.content) {
    const content = result.content as ContentstackGlobalNavGraphQLData;

    const navContent = content.all_global_navigation_v2.items[0];
    if (navContent) {
      return {
        topBannerMessages: adaptGraphQLTopBannerToCommon(
          navContent.top_banner_referenceConnection ?? { edges: [] },
        ),
        navContent: adaptGraphQLGlobalNavToCommonNav(navContent),
      };
    }
  }

  if (logger) {
    logger.error('no navigation content received');
  }

  throw new Error('no navigation data!');
};
